import React, { useState, useEffect } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { ModernSelectablePricingSection } from '../components/ModernSelectablePricingSection';
import { Loader2, AlertCircle, CheckCircle, X, Check, Crown, ArrowRight, Calendar, Zap, Info } from 'lucide-react';

// Tier definition
interface Tier {
  id: number;
  name: string;
  price: number;
  maxVodDuration: number;
  dailyVodUploadLimit: number;    // Added
  weeklyVodUploadLimit: number;   // Added
  monthlyVodUploadLimit: number;  // Added
  maxClipsPerVod: number;         // Added
  ffmpegResolution: string;
  ffmpegFps: number;
  editorResolution: string;
  editorFps: number;
  customAiPrompt: boolean;
  monthlyRedetectionLimit: number; // Added
  redetectWithNewPrompt?: boolean; // Yeni özellik eklendi
  // Removed: weeklyClipLimit, maxVodUploads
}

// TierCardProps definition
interface TierCardProps {
  tier: Tier;
  isActive: boolean;
  onSelect: () => void;
  isUpgradeDisallowed?: boolean; // Yeni özellik eklendi
  isFeatured?: boolean;
  isSideTier?: boolean; // For Free and Tier 3
}

// TierCard component with dark theme
const TierCard: React.FC<TierCardProps> = ({ tier, isActive, onSelect, isUpgradeDisallowed, isFeatured, isSideTier }) => {
  const formatDuration = (seconds: number) => {
    if (seconds >= 999999 || seconds === -1) return 'Unlimited';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatLimit = (limit: number) => {
    return limit >= 999999 || limit === -1 ? 'Unlimited' : limit.toString();
  };

  return (
    <div
      className={`flex flex-col h-full rounded-lg border ${
        isActive ? 'border-primary ring-2 ring-primary/50' : 'border-border'
      } bg-card text-foreground shadow-xl overflow-hidden transition-all duration-300 ease-in-out ${
        isFeatured
          ? 'scale-110 z-10 shadow-2xl ring-2 ring-purple-500 ring-offset-4 ring-offset-background relative' // Added relative for pseudo-element glow
          : ''
      } ${isSideTier ? 'scale-95' : ''}`}
    >
      {isFeatured && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-purple-600 to-pink-600 opacity-20 blur-xl -z-10"></div>
      )}
      <div className="p-6 flex-grow">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-foreground">{tier.name}</h3> {/* Theme text */}
          {isActive && (
            <span className="text-xs font-medium px-2.5 py-0.5 rounded bg-primary-lighter text-primary-dark"> {/* Theme primary chip */}
              Active Plan
            </span>
          )}
        </div>

        <p className="text-3xl font-bold text-primary mb-4"> {/* Theme primary text */}
          {tier.price === 0 ? 'Free' : `$${tier.price}/month`}
        </p>

        <hr className="my-4 border-border" /> {/* Theme border */}

        <ul className="mt-4 space-y-3 text-sm">
          <li className="flex items-center">
            {tier.maxVodDuration > 0 || tier.maxVodDuration === -1 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Max VOD Duration:</span>
            <strong className="ml-auto text-foreground">{formatDuration(tier.maxVodDuration)}</strong>
          </li>
          <li className="flex items-center">
            {tier.dailyVodUploadLimit > 0 || tier.dailyVodUploadLimit === -1 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Daily Video Uploads:</span>
            <strong className="ml-auto text-foreground">{formatLimit(tier.dailyVodUploadLimit)}</strong>
          </li>
          <li className="flex items-center">
            {tier.weeklyVodUploadLimit > 0 || tier.weeklyVodUploadLimit === -1 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Weekly Video Uploads:</span>
            <strong className="ml-auto text-foreground">{formatLimit(tier.weeklyVodUploadLimit)}</strong>
          </li>
          <li className="flex items-center">
            {tier.monthlyVodUploadLimit > 0 || tier.monthlyVodUploadLimit === -1 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Monthly Video Uploads:</span>
            <strong className="ml-auto text-foreground">{formatLimit(tier.monthlyVodUploadLimit)}</strong>
          </li>
          <li className="flex items-center">
            {tier.maxClipsPerVod > 0 || tier.maxClipsPerVod === -1 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Max Clips per VOD:</span>
            <strong className="ml-auto text-foreground">{formatLimit(tier.maxClipsPerVod)}</strong>
          </li>
          <li className="flex items-center">
            {tier.monthlyRedetectionLimit > 0 || tier.monthlyRedetectionLimit === -1 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Monthly Redetections:</span>
            <strong className="ml-auto text-foreground">{formatLimit(tier.monthlyRedetectionLimit)}</strong>
          </li>
          <li className="flex items-center">
            {tier.editorResolution !== "N/A" && tier.editorResolution !== "" ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Editor Resolution:</span>
            <strong className="ml-auto text-foreground">{tier.editorResolution}</strong>
          </li>
          <li className="flex items-center">
            {tier.editorFps > 0 ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Editor FPS:</span>
            <strong className="ml-auto text-foreground">{tier.editorFps}</strong>
          </li>
          <li className="flex items-center">
            {tier.customAiPrompt ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Custom AI Prompt:</span>
            <strong className="ml-auto text-foreground">{tier.customAiPrompt ? 'Yes' : 'No'}</strong>
          </li>
          <li className="flex items-center">
            {tier.redetectWithNewPrompt ? <Check className="h-4 w-4 mr-2 text-green-500 flex-shrink-0" /> : <X className="h-4 w-4 mr-2 text-red-500 flex-shrink-0" />}
            <span className="text-muted-foreground">Redetect Highlights with New Prompt:</span>
            <strong className="ml-auto text-foreground">{tier.redetectWithNewPrompt ? 'Yes' : 'No'}</strong>
          </li>
        </ul>
      </div>

      <div className="p-4 mt-auto border-t border-border"> {/* Theme border */}
        <button
          className={`w-full inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 ${
            isActive
              ? 'border border-primary text-primary hover:bg-primary/10' // Geist secondary/outline button
              : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md' // Geist primary button
          }`}
          onClick={onSelect}
          disabled={isActive || isUpgradeDisallowed}
        >
          {isActive ? 'Active Plan' : 'Select This Plan'}
        </button>
      </div>
    </div>
  );
};


// CircularProgress component for usage stats
interface CircularProgressProps {
  percentage: number;
  color: string;
  size?: number;
  strokeWidth?: number;
  displayText?: string; // To show remaining count or other text
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  percentage,
  color,
  size = 50, // Made it a bit smaller for compactness
  strokeWidth = 5,
  displayText,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  // Ensure offset calculation handles percentage > 100 or < 0 gracefully for visual representation
  const normalizedPercentage = Math.min(100, Math.max(0, percentage));
  const offset = circumference - (normalizedPercentage / 100) * circumference;

  return (
    <div className="relative flex flex-col items-center justify-center" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-300 dark:text-gray-600" // Adjusted dark mode background color
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className={color} // Progress circle color
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{ transition: 'stroke-dashoffset 0.3s ease-out' }}
        />
      </svg>
      {displayText && (
         <span className="absolute text-[10px] font-semibold text-foreground text-center">
          {displayText}
        </span>
      )}
    </div>
  );
};


// Updated UsageStats component with modern glassmorphism design
const UsageStats: React.FC = () => {
  const { usageInfo, activeSubscription } = useSubscription();

  if (!usageInfo || !activeSubscription) {
    return null;
  }

  const { limits, currentUsage, remaining } = usageInfo;

  // Helper to calculate remaining percentage, handling potential division by zero or zero limits
  const calculateRemainingPercentage = (rem: number, limit: number): number => {
    if (limit <= 0 || limit >= 999999 || limit === -1) return 100;
    return Math.min(100, Math.max(0, (rem / limit) * 100));
  };

  const formatDisplayText = (rem: number, limit: number): string => {
    if (limit >= 999999 || limit === -1) return '∞';
    if (limit <= 0) return 'N/A';
    return `${rem}`;
  };

  const dailyVodRemainingPercentage = calculateRemainingPercentage(remaining.dailyVods, limits.dailyVodUploadLimit);
  const weeklyVodRemainingPercentage = calculateRemainingPercentage(remaining.weeklyVods, limits.weeklyVodUploadLimit);
  const monthlyVodRemainingPercentage = calculateRemainingPercentage(remaining.monthlyVods, limits.monthlyVodUploadLimit);
  const redetectionRemainingPercentage = calculateRemainingPercentage(remaining.monthlyRedetections, limits.monthlyRedetectionLimit);

  const usageItems = [
    {
      label: 'Daily VODs',
      percentage: dailyVodRemainingPercentage,
      color: 'text-sky-500',
      remaining: remaining.dailyVods,
      limit: limits.dailyVodUploadLimit,
      displayText: formatDisplayText(remaining.dailyVods, limits.dailyVodUploadLimit)
    },
    {
      label: 'Weekly VODs',
      percentage: weeklyVodRemainingPercentage,
      color: 'text-lime-500',
      remaining: remaining.weeklyVods,
      limit: limits.weeklyVodUploadLimit,
      displayText: formatDisplayText(remaining.weeklyVods, limits.weeklyVodUploadLimit)
    },
    {
      label: 'Monthly VODs',
      percentage: monthlyVodRemainingPercentage,
      color: 'text-amber-500',
      remaining: remaining.monthlyVods,
      limit: limits.monthlyVodUploadLimit,
      displayText: formatDisplayText(remaining.monthlyVods, limits.monthlyVodUploadLimit)
    },
    {
      label: 'Redetections',
      percentage: redetectionRemainingPercentage,
      color: 'text-violet-500',
      remaining: remaining.monthlyRedetections,
      limit: limits.monthlyRedetectionLimit,
      displayText: formatDisplayText(remaining.monthlyRedetections, limits.monthlyRedetectionLimit)
    },
  ];

  // Get tier color for gradient
  const getTierGradient = (tierName: string) => {
    switch (tierName) {
      case 'Tier 2':
        return 'from-blue-500/20 via-cyan-500/20 to-blue-500/20';
      case 'Tier 3':
        return 'from-purple-500/20 via-pink-500/20 to-purple-500/20';
      default:
        return 'from-gray-500/20 via-slate-500/20 to-gray-500/20';
    }
  };

  const getTierColor = (tierName: string) => {
    switch (tierName) {
      case 'Tier 2':
        return 'text-blue-400';
      case 'Tier 3':
        return 'text-purple-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="relative group mb-8">
      {/* Background gradient glow */}
      <div className={`
        absolute -inset-0.5 bg-gradient-to-r ${getTierGradient(activeSubscription?.tier.name || '')} 
        rounded-2xl opacity-30 blur-xl group-hover:opacity-50 transition-opacity duration-300
      `} />
      
      <div className="
        relative bg-black/40 backdrop-blur-xl border border-white/10 
        rounded-2xl overflow-hidden shadow-2xl
        hover:bg-black/50 hover:border-white/20 transition-all duration-300
      ">
        <div className="p-6 md:p-8">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6">
            {/* Left: Title and Plan Info */}
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center gap-3 mb-2">
                <div className={`
                  p-2 rounded-xl bg-gradient-to-r ${getTierGradient(activeSubscription?.tier.name || '')} 
                  border border-white/10
                `}>
                  <Crown size={20} className={getTierColor(activeSubscription?.tier.name || '')} />
                </div>
                <h3 className="text-xl font-semibold text-white">Subscription Status & Usage</h3>
              </div>
              <div className="flex items-center gap-3">
                <p className="text-white/70">
                  Current Plan: <span className="font-medium text-white">{activeSubscription.tier.name}</span>
                </p>
                <div className={`
                  inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium
                  bg-gradient-to-r ${activeSubscription?.tier.name === 'Tier 2' ? 'from-blue-500 to-cyan-500' : 
                    activeSubscription?.tier.name === 'Tier 3' ? 'from-purple-500 to-pink-500' : 'from-gray-500 to-gray-600'} 
                  text-white shadow-lg
                `}>
                  <Crown size={10} />
                  {activeSubscription.tier.name}
                </div>
              </div>
            </div>

            {/* Right: Renewal Date */}
            <div className="text-right">
              <p className="text-xs text-white/50 uppercase tracking-wide font-medium">Renews On</p>
              <p className="text-sm text-white font-medium">
                {activeSubscription?.endDate ? 
                  new Date(activeSubscription.endDate).toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                  }) : 'N/A'
                }
              </p>
            </div>
          </div>

          {/* Usage Statistics */}
          <div className="space-y-4">
            {/* Usage Items Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {usageItems.map((item) => (
                (item.limit > 0 || item.limit >= 999999 || item.limit === -1) && (
                  <div key={item.label} className="
                    flex flex-col items-center p-4 rounded-xl
                    bg-white/5 border border-white/10 hover:bg-white/10 
                    transition-all duration-200 group/item
                  ">
                    <p className="text-xs font-medium text-white/60 mb-3 text-center">
                      {item.label}
                    </p>
                    <CircularProgress
                      percentage={item.percentage}
                      color={item.color}
                      displayText={item.displayText}
                      size={60}
                      strokeWidth={5}
                    />
                    {item.limit < 999999 && item.limit > 0 && item.limit !== -1 && (
                      <p className="text-xs text-white/50 mt-3 text-center">
                        {item.remaining} / {item.limit}
                      </p>
                    )}
                    {item.limit === -1 && (
                      <p className="text-xs text-white/50 mt-3 text-center">
                        Unlimited
                      </p>
                    )}
                  </div>
                )
              ))}
              
              {/* Max Clips per VOD */}
              <div className="
                flex flex-col items-center p-4 rounded-xl
                bg-white/5 border border-white/10 hover:bg-white/10 
                transition-all duration-200
              ">
                <p className="text-xs font-medium text-white/60 mb-3 text-center">
                  Max Clips/VOD
                </p>
                <div className="flex items-center justify-center h-[60px]">
                  <p className="text-2xl font-bold text-white">
                    {limits.maxClipsPerVod >= 999999 || limits.maxClipsPerVod === -1 ? '∞' : limits.maxClipsPerVod}
                  </p>
                </div>
                <p className="text-xs text-white/50 mt-3 opacity-0">
                  0 / 0
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


// Main SubscriptionPage component with dark theme
function SubscriptionPage() {
  const { user } = useAuth();
  // Get the updated context values
  const {
    tiers,
    activeSubscription,
    usageInfo, // Get the new usageInfo state
    isLoading,
    createSubscription,
    verifyPayment,
    changeTier
  } = useSubscription();

  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Payment Dialog State
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentId, setPaymentId] = useState<string | null>(null);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  // Change Tier Dialog State
  const [changeTierDialogOpen, setChangeTierDialogOpen] = useState(false);

  // Set initial selected tier
  useEffect(() => {
    if (activeSubscription) {
      setSelectedTier(activeSubscription.tierId);
    }
  }, [activeSubscription]);


  const handleVerifyPayment = async () => {
    if (!paymentId) return;
    setVerifyingPayment(true);
    setError(null);
    try {
      const result = await verifyPayment(paymentId);
      if (result.success) {
        setPaymentVerified(true);
        setSuccess('Payment successfully verified and your subscription is activated.');
      } else {
        setError(result.message || 'Payment could not be verified.');
        setPaymentVerified(false);
      }
    } catch (err) {
      setError('An error occurred while verifying payment.');
      setPaymentVerified(false);
      console.error('Payment verification error:', err);
    } finally {
      setVerifyingPayment(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!selectedTier) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await createSubscription(selectedTier);
      if (result.success) {
        if (result.paymentId) {
          setPaymentId(result.paymentId);
          setPaymentVerified(false);
          setPaymentDialogOpen(true);
        } else {
          setSuccess('Your subscription was successfully created.');
        }
      } else {
        setError(result.message || 'Subscription could not be created.');
      }
    } catch (err) {
      setError('An error occurred while creating the subscription.');
      console.error('Subscription creation error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangeTier = async (immediate: boolean = false) => {
    if (!selectedTier || !activeSubscription) return;
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await changeTier(activeSubscription.id, selectedTier, immediate);
      if (result.success) {
        setSuccess(result.message || 'Your subscription plan was successfully changed.');
        setChangeTierDialogOpen(false);
      } else {
        setError(result.message || 'Subscription plan could not be changed.');
      }
    } catch (err) {
      setError('An error occurred while changing the subscription plan.');
      console.error('Tier change error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSelectTier = (tierId: number) => {
    if (isProcessing || verifyingPayment) return;

    // Tier 1'den (id: 1) Tier 2 (id: 2) veya Tier 3 (id: 3) e geçişi engelle
    // Bu kontrol, backend'de de yapılmalıdır.
    // Tier ID'lerinin 1, 2, 3 olduğunu varsayıyoruz.
    // Eğer aktif abonelik Tier 1 ise ve seçilen tier 1'den büyükse, işlemi durdur.
    const currentTierIsTier1 = activeSubscription?.tierId === tiers.find(t => t.price === 0)?.id; // Ücretsiz tier'ı Tier 1 olarak kabul edelim.
    const selectedTierIsHigher = tierId > (tiers.find(t => t.price === 0)?.id || 0);

    if (currentTierIsTier1 && selectedTierIsHigher) {
      setError("Tier 1'den daha yüksek bir tier'a geçiş şu anda mümkün değildir.");
      // Buton zaten disabled olmalı, ancak ekstra bir güvenlik katmanı.
      return;
    }

    const newlySelectedTierId = tierId;

    if (activeSubscription?.tierId === newlySelectedTierId) {
        return;
    }

    setSelectedTier(newlySelectedTierId);

    if (activeSubscription && activeSubscription.tierId !== newlySelectedTierId) {
      setError(null);
      setSuccess(null);
      setChangeTierDialogOpen(true);
    }
  };

  const closePaymentDialog = () => {
      if (!verifyingPayment) {
          setPaymentDialogOpen(false);
          if (!paymentVerified) {
              setPaymentId(null);
              if (!activeSubscription) {
                  setSelectedTier(null);
              }
          }
      }
  }

  const closeChangeTierDialog = () => {
      if (!isProcessing) {
          setChangeTierDialogOpen(false);
          setSelectedTier(activeSubscription?.tierId ?? null);
          setError(null);
      }
  }


  if (isLoading) {
    // Apply dark theme to loading state
    return (
      <div className="max-w-screen-lg mx-auto px-4 mt-4 mb-4 text-foreground min-h-screen"> {/* Theme text */}
        <div className="flex justify-center items-center h-[50vh]">
          <Loader2 className="h-12 w-12 animate-spin text-primary" /> {/* Theme primary */}
        </div>
      </div>
    );
  }

  return (
    // Apply dark theme to main container
    <div className="max-w-screen-lg mx-auto px-4 mt-4 mb-4 text-foreground min-h-screen"> {/* Theme text */}
      <h1 className="text-3xl font-bold mb-6 text-foreground"> {/* Theme text */}
        Subscription Plans
      </h1>

      {/* Alert replacements - Use Geist theme colors */}
      {error && !changeTierDialogOpen && !paymentDialogOpen && (
        <div className="mb-4 p-4 border rounded-md bg-error-lighter border-error-light text-error-dark" role="alert"> {/* Geist error colors */}
          <div className="flex items-center">
             <AlertCircle className="h-5 w-5 mr-2" />
             <p className="text-sm font-medium">{error}</p>
          </div>
        </div>
      )}
      {success && !changeTierDialogOpen && !paymentDialogOpen && (
        <div className="mb-4 p-4 border rounded-md bg-success-lighter border-success-light text-success-dark" role="alert"> {/* Geist success colors */}
           <div className="flex items-center">
             <CheckCircle className="h-5 w-5 mr-2" />
             <p className="text-sm font-medium">{success}</p>
           </div>
        </div>
      )}

      {activeSubscription && <UsageStats />}

      {/* Modern Selectable Pricing Section */}
      <div className="mb-8">
        <ModernSelectablePricingSection 
          tiers={tiers}
          selectedTier={selectedTier}
          activeTier={activeSubscription?.tierId}
          onSelectTier={handleSelectTier}
          isUpgradeDisallowed={(tierId) => {
            const tier1Id = tiers.find(t => t.price === 0)?.id;
            const isCurrentlyOnTier1 = activeSubscription?.tierId === tier1Id;
            const isCardForHigherTier = tier1Id !== undefined && tierId > tier1Id;
            return !!(isCurrentlyOnTier1 && isCardForHigherTier);
          }}
          compact={true}
        />
      </div>

      {/* Create Subscription Button Area */}
      {selectedTier && !activeSubscription && tiers.find(t => t.id === selectedTier) && (
        <div className="mt-6 flex justify-center">
          {/* Geist primary button */}
          <button
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md h-11 px-8"
            onClick={handleCreateSubscription}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <Loader2 className="h-5 w-5 animate-spin text-primary-foreground" />
            ) : (
               `Start Subscription (${tiers.find(t => t.id === selectedTier)?.name ?? 'Selected Plan'})`
            )}
          </button>
        </div>
      )}

      {/* Payment Verification Dialog */}
      {paymentDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" aria-labelledby="payment-dialog-title" role="dialog" aria-modal="true">
           <div className="bg-card text-foreground rounded-lg shadow-xl p-6 max-w-md w-full mx-4 border border-border" onClick={(e) => e.stopPropagation()}> {/* Theme card, text, border */}
             <h3 id="payment-dialog-title" className="text-lg font-semibold mb-3 text-foreground">Payment Verification</h3> {/* Theme text */}
              {error && (
                  <div className="mb-3 p-3 border rounded-md bg-error-lighter border-error-light text-error-dark" role="alert"> {/* Geist error colors */}
                      <div className="flex items-center">
                          <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          <p className="text-xs">{error}</p>
                      </div>
                  </div>
              )}
             <div className="mb-4">
               <p className="text-sm text-muted-foreground"> {/* Theme muted text */}
                 {paymentVerified
                   ? 'Your payment has been successfully verified and your subscription is activated.'
                   : 'Please click the "Verify Payment" button to verify your payment.'}
               </p>
               {verifyingPayment && (
                  <div className="w-full bg-accents-2 rounded-full h-1.5 mt-3 overflow-hidden"> {/* Theme accent for progress bg */}
                     <div className="h-1.5 rounded-full bg-primary animate-pulse"></div> {/* Theme primary for progress */}
                  </div>
               )}
             </div>
             <div className="flex justify-end space-x-2">
               {paymentVerified ? (
                 <button onClick={closePaymentDialog} className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3"> {/* Geist primary button */}
                   OK
                 </button>
               ) : (
                 <>
                   <button onClick={closePaymentDialog} disabled={verifyingPayment} className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-border bg-background hover:bg-accents-1 text-foreground h-9 px-3"> {/* Geist secondary button */}
                     Cancel
                   </button>
                   <button onClick={handleVerifyPayment} disabled={verifyingPayment} className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-3 min-w-[120px]"> {/* Geist primary button */}
                     {verifyingPayment ? <Loader2 className="h-4 w-4 animate-spin mx-auto text-primary-foreground" /> : 'Verify Payment'}
                   </button>
                 </>
               )}
             </div>
           </div>
         </div>
      )}

       {/* Modern Change Tier Dialog */}
       {changeTierDialogOpen && (
         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" aria-labelledby="change-tier-dialog-title" role="dialog" aria-modal="true">
           <div className="relative group max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
             {/* Background gradient glow */}
             <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-cyan-500/20 rounded-2xl opacity-50 blur-xl group-hover:opacity-70 transition-opacity duration-300" />
             
             <div className="
               relative bg-black/40 backdrop-blur-xl border border-white/10 
               rounded-2xl overflow-hidden shadow-2xl
               hover:bg-black/50 hover:border-white/20 transition-all duration-300
             ">
               <div className="p-6 md:p-8">
                 {/* Header */}
                 <div className="flex items-center gap-3 mb-6">
                   <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-white/10">
                     <Crown size={20} className="text-purple-400" />
                   </div>
                   <h3 id="change-tier-dialog-title" className="text-xl font-semibold text-white">Change Subscription Plan</h3>
                 </div>

                 {/* Error Alert */}
                 {error && (
                   <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 backdrop-blur-sm" role="alert">
                     <div className="flex items-center gap-3">
                       <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
                       <p className="text-sm text-red-300">{error}</p>
                     </div>
                   </div>
                 )}

                 {/* Plan Change Info */}
                 <div className="mb-6">
                   <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 mb-4">
                     <Info size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
                     <div>
                       <p className="text-white/90 font-medium mb-1">Plan Change Options</p>
                       <p className="text-white/70 text-sm leading-relaxed">
                         Choose when you'd like your plan change to take effect. You can apply it immediately or wait until your current billing period ends.
                       </p>
                     </div>
                   </div>

                   {/* Current vs New Plan */}
                   {activeSubscription && selectedTier && (
                     <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                       <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                         <p className="text-xs text-white/50 uppercase tracking-wide font-medium mb-1">Current Plan</p>
                         <p className="text-white font-semibold">{activeSubscription.tier.name}</p>
                         <p className="text-white/60 text-sm">${activeSubscription.tier.price}/month</p>
                       </div>
                       <div className="p-4 rounded-xl bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30">
                         <p className="text-xs text-purple-300 uppercase tracking-wide font-medium mb-1">New Plan</p>
                         <p className="text-white font-semibold">{tiers.find(t => t.id === selectedTier)?.name}</p>
                         <p className="text-purple-300 text-sm">${tiers.find(t => t.id === selectedTier)?.price}/month</p>
                       </div>
                     </div>
                   )}
                 </div>

                 {/* Processing Indicator */}
                 {isProcessing && (
                   <div className="mb-6">
                     <div className="flex items-center gap-3 p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                       <Loader2 className="h-5 w-5 text-yellow-400 animate-spin flex-shrink-0" />
                       <p className="text-yellow-300 text-sm">Processing your plan change...</p>
                     </div>
                   </div>
                 )}

                 {/* Action Buttons */}
                 <div className="flex flex-col sm:flex-row gap-3">
                   {/* Cancel Button */}
                   <button 
                     onClick={closeChangeTierDialog} 
                     disabled={isProcessing} 
                     className="
                       flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium
                       bg-white/10 border border-white/20 text-white
                       hover:bg-white/15 hover:border-white/30 disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200
                     "
                   >
                     <X size={16} />
                     Cancel
                   </button>

                   {/* Change at End of Period */}
                   <button 
                     onClick={() => handleChangeTier(false)} 
                     disabled={isProcessing} 
                     className="
                       group/btn flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium
                       bg-gradient-to-r from-blue-500 to-cyan-500 text-white
                       hover:from-blue-600 hover:to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105
                     "
                   >
                     {isProcessing ? (
                       <Loader2 className="h-4 w-4 animate-spin" />
                     ) : (
                       <>
                         <Calendar size={16} />
                         Change at Period End
                         <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                       </>
                     )}
                   </button>

                   {/* Change Immediately */}
                   <button 
                     onClick={() => handleChangeTier(true)} 
                     disabled={isProcessing} 
                     className="
                       group/btn flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-medium
                       bg-gradient-to-r from-purple-500 to-pink-500 text-white
                       hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed
                       shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105
                     "
                   >
                     {isProcessing ? (
                       <Loader2 className="h-4 w-4 animate-spin" />
                     ) : (
                       <>
                         <Zap size={16} />
                         Change Immediately
                         <ArrowRight size={14} className="group-hover/btn:translate-x-0.5 transition-transform" />
                       </>
                     )}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
    </div>
  );
}

export default SubscriptionPage;